import { collectEvent, UserAction, userActionTypeEnum } from '@thoughtspot/metrics';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getAnswerPageConfig } from '/@contexts/answer-context';
import { BlinkErrorCode } from '/@utils/error-handling/blink-error-boundary';
import {
    DocumentNode,
} from '../../utils/error-handling/apollo-hooks-wrappers/custom-apollo-hooks';
import {
    useBachMutation,
} from './bach';
import {
    BachMutationFunctionOptions,
    BachMutationHookOptions,
    BachMutationTuple,
} from './bach.util';

export function useHydrationGate() {
    let isHydrated = true;
    try {
        ({ isHydrated } = getAnswerPageConfig());
    } catch {
        // Outside answer-context; treat as already hydrated.
    }
    const isReady = isHydrated !== false;
    const isReadyRef = useRef(isReady);
    isReadyRef.current = isReady;

    const pendingCallRef = useRef<(() => void) | null>(null);
    const [isPending, setIsPending] = useState(false);

    useEffect(() => {
        if (isReady && pendingCallRef.current) {
            const call = pendingCallRef.current;
            pendingCallRef.current = null;
            setIsPending(false);
            call();
        }
    }, [isReady]);

    const gateCall = useCallback(
        <T>(execute: () => Promise<T>): Promise<T> => {
            if (isReadyRef.current) {
                return execute();
            }
            return new Promise<T>((resolve, reject) => {
                pendingCallRef.current = () => {
                    // eslint-disable-next-line promise/catch-or-return
                    execute().then(resolve, reject);
                };
                setIsPending(true);
            });
        },
        [],
    );

    return { isReady, isWaiting: isPending, gateCall };
}

/**
 * Hydration-aware wrapper over useBachMutation.
 * Defers the mutation call until isHydrated is true, reporting
 * loading while waiting.
 */
export function useGatedBachMutation<TData, TVariables>(
    sessionId: string,
    mutation: DocumentNode,
    options: BachMutationHookOptions<TData, TVariables> = {},
    handledErrors?: BlinkErrorCode[],
    isPinboardSession = false,
    sendAcSessionInfo = false,
): BachMutationTuple<
    TData,
    Omit<TVariables, 'session' | 'dataPaginationParams'>
> {
    const { isReady, isWaiting, gateCall } = useHydrationGate();
    const [mutate, state] = useBachMutation(
        sessionId,
        mutation,
        options,
        handledErrors,
        isPinboardSession,
        sendAcSessionInfo,
    );

    const mutationName = (mutation.definitions[0] as any)?.name?.value;

    const mutateRef = useRef(mutate);
    mutateRef.current = mutate;
    const gatedMutate = useCallback(
        (opts: BachMutationFunctionOptions<TData, TVariables>) => {
            if (!isReady) {
                collectEvent(
                    new UserAction(
                        userActionTypeEnum.GATED_BACH_MUTATION_DEFERRED,
                    ),
                    { mutationName },
                );
            }
            return gateCall(() => mutateRef.current(opts));
        },
        [gateCall, isReady, mutationName],
    );

    const result = useMemo(
        () => (isWaiting ? { ...state, loading: true } : state),
        [isWaiting, state],
    );

    return [gatedMutate, result];
}